import { useRef, useState } from 'react';
import { parseCSV } from '@freight-cbm/shared-logic';
import { useAppStore } from '@/store/store';
import type { DimensionUnit, WeightUnit } from '@freight-cbm/shared-types';
import { NotificationBanner } from '@/components/shared/NotificationBanner';
import type { BannerType } from '@/components/shared/NotificationBanner';

export function ImportButton() {
  const fileRef = useRef<HTMLInputElement>(null);
  const addPackage = useAppStore((s) => s.addPackage);
  const [banner, setBanner] = useState<{ message: string; type: BannerType } | null>(null);

  const handleImport = () => {
    fileRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const result = parseCSV(text);

      if (result.errors.length > 0) {
        setBanner({ message: `Import errors: ${result.errors.join('; ')}`, type: 'error' });
        return;
      }

      if (result.warnings.length > 0) {
        console.warn('CSV import warnings:', result.warnings);
      }

      // Clear existing packages
      const store = useAppStore.getState();
      store.clearPackages();

      let imported = 0;
      for (const row of result.lines) {
        if (row.length === undefined || row.width === undefined || row.height === undefined || row.weight === undefined) {
          continue;
        }

        addPackage({
          length: row.length,
          width: row.width,
          height: row.height,
          weight: row.weight,
          quantity: row.quantity ?? 1,
          dimUnit: (row.dimUnit as DimensionUnit) || 'cm',
          weightUnit: (row.weightUnit as WeightUnit) || 'kg',
        });
        imported++;
      }

      if (imported === 0) {
        setBanner({ message: 'No valid package lines found in CSV.', type: 'warning' });
      } else {
        setBanner({ message: `Imported ${imported} package${imported !== 1 ? 's' : ''} successfully.`, type: 'success' });
      }
    } catch (err) {
      setBanner({ message: `Failed to read file: ${(err as Error).message}`, type: 'error' });
    }

    // Reset input so the same file can be re-imported
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
      {banner && (
        <NotificationBanner
          message={banner.message}
          type={banner.type}
          onDismiss={() => setBanner(null)}
        />
      )}
      <button type="button" className="btn btn-sm" onClick={handleImport} title="Import CSV">
        Import CSV
      </button>
      <input
        ref={fileRef}
        type="file"
        accept=".csv,text/csv"
        style={{ display: 'none' }}
        onChange={handleFileChange}
        aria-hidden="true"
      />
    </div>
  );
}
